/**
 * StatCard — a single KPI/stat card: a large value, a title, and an optional
 * footnote (same size as the title but lighter, pinned to the bottom). Built on
 * the Fluent `Card` so it shares the app's card styling. Used on the Dashboard
 * via <StatCardGrid>.
 *
 *   <StatCard value={20} title="Total Submissions" />
 *   <StatCard value={8} title="Live" footnote="+2 this month" />
 *
 * The value + title are centered as a block within the full card; the footnote
 * is pinned to the bottom and does not affect that centering, so the value/title
 * stay dead-center whether or not a footnote is shown. Pass `onClick` to make the
 * whole card an interactive button.
 */

import { Card, Text, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import type { ReactNode } from 'react';

const useStyles = makeStyles({
  // Center the value+title block vertically (and horizontally) in the full
  // card. The footnote is positioned absolutely at the bottom so it never
  // shifts that centered block — the value/title sit dead-center whether or
  // not a footnote is present.
  card: {
    position: 'relative',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    // Floor tall enough that the centered value/title block clears the
    // bottom-pinned footnote (which is out of flow) and never overlaps it.
    minHeight: '140px',
    // Small screens: shorter cards so a 2-up row isn't oversized.
    '@media (max-width: 640px)': {
      minHeight: '104px',
    },
  },
  clickable: { cursor: 'pointer' },
  // Tight line box on the big number so the title sits just beneath it.
  value: { lineHeight: 1 },
  // Pull the title up to tighten the gap left by the value/title line boxes,
  // so the title sits just beneath the value.
  title: { marginTop: `calc(-1 * ${tokens.spacingVerticalXXS})` },
  // Footnote pinned to the bottom of the card, lighter than the title. Its
  // absolute positioning keeps it out of the centered value/title flow.
  footnote: {
    position: 'absolute',
    left: tokens.spacingHorizontalL,
    right: tokens.spacingHorizontalL,
    bottom: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
  },
});

interface StatCardProps {
  /** The large headline figure. */
  value: ReactNode;
  /** Label under the value. */
  title: ReactNode;
  /** Optional sub-line pinned to the bottom of the card. */
  footnote?: ReactNode;
  /** Makes the whole card an interactive button. */
  onClick?: () => void;
  className?: string;
}

export function StatCard({ value, title, footnote, onClick, className }: StatCardProps) {
  const styles = useStyles();
  return (
    <Card
      className={mergeClasses(styles.card, onClick && styles.clickable, className)}
      onClick={onClick}
    >
      <Text size={800} weight="semibold" block align="center" className={styles.value}>{value}</Text>
      <Text size={300} block align="center" className={styles.title}>{title}</Text>
      {footnote != null && (
        <Text size={300} block align="center" className={styles.footnote}>{footnote}</Text>
      )}
    </Card>
  );
}

export default StatCard;
